import {Box, Divider, Stack, Switch, Tab, Tabs} from "@mui/material";
import TextField from "@mui/material/TextField";
import {useNavigate, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import SettingsEntityHeader from "../../layout/SettingsEntityHeader";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import Property from "crqlar-react-common/dist/components/Property";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {HobexConfig} from "crqlar-react-common/dist/models/integrations/hobex/hobexConfig.model";
import {HobexConfigService} from "crqlar-react-common/dist/services/integrations/hobex/hobexConfig.service";
import HobexLogs from "./HobexLogs.component";
import EmptyState from "crqlar-react-common/dist/components/EmptyState";
import Button from "@mui/material/Button";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";

const HobexConfigurationDetails = () => {

    useTrackScreenVisit('hobex configuration details')

    //region State

    const {t} = useTranslation()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['config', 'logs'], 'config');
    const setActiveTab = (newTab: string) => {
        setSearchParams({tab: newTab});
    };

    const hobexConfigData = useEntity({
        get: async () => {
            return await HobexConfigService.get()
        },
        update: HobexConfigService.save,
        create: HobexConfigService.save,
        delete: (id) => HobexConfigService.delete({id: [id]}),
    })

    const config = hobexConfigData.item
    const setConfig = hobexConfigData.setItem

    const handleCreate = () => hobexConfigData.startCreate()

    //endregion Sate

    //region Handlers

    const updateField = (field: keyof HobexConfig, value: any) => {
        setConfig({...config, [field]: value})
    }

    //endregion Handlers

    //region UI

    const headerSection = (
        <SettingsEntityHeader
            title={t('Hobex account')}
            data={hobexConfigData}
            back={() => navigate('/system')}
        />
    )

    const credentialsSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Credentials')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Merchant id')}
                    tooltip={t('Merchant identifier provided by Hobex for this account.')}
                >
                    <TextField
                        id={'merchantId'}
                        size={'small'}
                        value={config.merchantId || ''}
                        onChange={(e) => updateField('merchantId', e.target.value)}
                        fullWidth
                        required
                    />
                </Property>

                <Property
                    title={t('Authorization token')}
                    tooltip={t('Credential used to authorize requests to Hobex.')}
                >
                    <TextField
                        id={'authorizationToken'}
                        size={'small'}
                        value={config.authorizationToken || ''}
                        onChange={(e) => updateField('authorizationToken', e.target.value)}
                        fullWidth
                        required
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const configurationSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Configuration')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Enabled')}
                    tooltip={t('Turns the Hobex integration on or off.')}
                >
                    <Switch
                        id={'enabled'}
                        checked={config.enabled}
                        onChange={(e) => updateField('enabled', e.target.checked)}
                    />
                </Property>

                <Property
                    title={t('Test mode')}
                    tooltip={t('Uses Hobex test behavior without treating payments as live production transactions.')}
                >
                    <Switch
                        id={'test-mode'}
                        checked={config.testMode}
                        onChange={(e) => updateField('testMode', e.target.checked)}
                    />
                </Property>

                <Property
                    title={t('Target sandbox')}
                    tooltip={t('Sends Hobex requests to the sandbox endpoint instead of the production endpoint.')}
                >
                    <Switch
                        id={'target-sandbox'}
                        checked={config.targetSandbox}
                        onChange={(e) => updateField('targetSandbox', e.target.checked)}
                    />
                </Property>

                <Property
                    title={t('Brands')}
                    tooltip={t('Space-separated card brands accepted by this Hobex setup.')}
                >
                    <TextField
                        id={'brands'}
                        size={'small'}
                        value={config.brands || ''}
                        onChange={(e) => updateField('brands', e.target.value)}
                        fullWidth
                        required
                        helperText={t('Space separated list of brands (by default: VISA MASTER AMEX)')}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const emptyState = (
        <EmptyState
            icon={<ElectricalServicesIcon/>}
            title={t('No integration configured')}
            description={t('Create the configuration to connect this integration.')}
        >
            <Button variant={'contained'} onClick={handleCreate}>
                {t('Create')}
            </Button>
        </EmptyState>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete integration')}
                description={t('This permanently deletes the integration configuration and stops its syncing immediately. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={hobexConfigData.saving}
                onDelete={() => hobexConfigData.removeNow()}
                reloadOnDelete
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {config.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const configTab = (
        <Stack
            sx={{
                width: '100%',
                gap: 6,
            }}
        >
            {credentialsSection}
            <Divider/>
            {configurationSection}

            {
                !!config.id &&
                <>
                    <Divider/>
                    {dangerZoneSection}
                </>
            }
        </Stack>
    )

    //endregion UI

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {
                    hobexConfigData.isMissing ? emptyState : (
                        <>
                            <Tabs
                                value={activeTab}
                                onChange={(e, v) => setActiveTab(v)}
                                aria-label="tabs"
                                sx={{
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                }}
                            >
                                <Tab label={t('Configuration')} value={'config'}/>
                                <Tab label={t('Stats and Logs')} value={'logs'}/>
                            </Tabs>

                            {activeTab === 'config' && configTab}
                            {activeTab === 'logs' && <HobexLogs/>}
                        </>
                    )
                }
            </Stack>
        </Box>
    );
}

export default HobexConfigurationDetails;
